import { tap } from 'rxjs/operators';
import { inject, isDevMode } from '@angular/core';
import { HttpInterceptorFn, HttpResponse } from '@angular/common/http';

/**
 * enums
 */
import { AppRequestUtils } from './utilities';
import { ILayoutLang, LayoutService } from '@core/services';

/**
 * App Request HTTP Interceptor
 * Automatically adds Accept-Language header and logs responses in development environment.
 * Also adds the App Host URL to the request.
 * @function appRequestInterceptor
 * @description Handles app-specific request modifications including:
 * - Language headers (Accept-Language)
 * - Response logging (development only)
 * - App Host URL (if the request is an app request)
 * - Future app-specific configurations
 * 
 * @param {HttpRequest<any>} req - The outgoing HTTP request
 * @param {HttpHandlerFn} next - The next interceptor in the chain
 * @returns {Observable<HttpEvent<any>>} The HTTP response with app-specific modifications
 * 
 * @example
 * ```typescript
 * // Automatically adds Accept-Language header and logs responses
 * // GET /api/users -> Accept-Language: en-US + logs response in dev
 * // POST /api/users -> Accept-Language: ar-SA + logs response in dev
 * // GET /api/users -> App Host URL: https://api.example.com/api/users
 * ```
 * 
 * @since 1.0.0
 * @author SADIN Development Team
 */
export const appRequestInterceptor: HttpInterceptorFn = (req, next) => {

  // Inject layout service to get current language
  const _layoutService = inject(LayoutService);

  // get current language
  const _lang = _layoutService.layoutConfigValue.lang as ILayoutLang;

  // Clone the request
  let appReq = req.clone();

  // Add the Accept-Language header
  appReq = AppRequestUtils.addAcceptLanguageHeader(appReq, _lang);

  // Add the App Host URL
  appReq = AppRequestUtils.addAppHostUrl(appReq);
  
  // Process the request and handle response
  return next(appReq).pipe(
    tap({
      next: (event) => {
        // Only log in development environment
        if (isDevMode()) {
          if (event instanceof HttpResponse) {
            AppRequestUtils.logResponse(req, event);
          }
        }
      },
    }),
  );
};
